import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CheckCircleIcon,
  CircleDashedIcon,
  CircleIcon,
  PencilSimpleLineIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { toast } from 'sonner';

import { ArticleJobProgress } from './ArticleJobProgress';
import { ArticlePromoteActions } from './ArticlePromoteActions';
import {
  AddressRefusalDetail,
  type AddressRefusalView,
  EditorialAddressed,
} from './EditorialAddressed';
import { EditorialFactCheck } from './EditorialFactCheck';
import { EditorialScore } from './EditorialTopicTable';
import { ImproveArticleDialog } from './ImproveArticleDialog';
import { TagNoteButton } from './TagNoteButton';
import { versionLabel } from './editorial-article-header';
import { findLinkedFindingKey } from './editorial-note-link';
import { recheckAction } from './recheck-action';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CopyButton } from '@/components/ui/copy-button';
import { InlineFieldEdit } from '@/components/ui/inline-field-edit';
import { SegmentedToggle, type SegmentedToggleOption } from '@/components/ui/segmented-toggle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useArticlePublicationStates } from '@/hooks/reach/useArticleHandoff';
import { useArticleReviewJob } from '@/hooks/reach/useArticleReviewJob';
import {
  type ArticleStep,
  type ArticleStepState,
  type WebReadinessFindingSummary,
  articleCritiqueFindingKey,
  normalizeReviewedFindingKeys,
  useEditorialArticleTrail,
} from '@/hooks/reach/useEditorialArticleTrail';
import { useEditorialAutoAddress } from '@/hooks/reach/useEditorialAutoAddress';
import { useEditorialMap } from '@/hooks/reach/useEditorialMap';
import { useUpdateEditorialTopic } from '@/hooks/reach/useUpdateEditorialTopic';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import {
  ADDRESS_REFUSAL_WORDS,
  type AddressRejectionRecord,
  type AutoAddressMode,
  addressPassCoversCurrentRules,
} from '@/lib/content-doctrine/article-address';
import { stillOpenAfterAddress } from '@/lib/content-doctrine/article-address-carry';
import { isSingleSentence } from '@/lib/content-doctrine/article-address-passage';
import { addressNoteCounts, readScopedReview } from '@/lib/content-doctrine/article-address-scope';
import {
  type ArticleCritiqueReport,
  critiqueDimensionLabel,
} from '@/lib/content-doctrine/article-critique';
import {
  type ClaimCheckConfirmation,
  confirmationNote,
  decidedFindingKeys,
} from '@/lib/content-doctrine/article-critique-confirmation';
import { partitionCritiqueFindings } from '@/lib/content-doctrine/article-critique-limits';
import {
  buildFactCheckFixFeedback,
  factCheckFixItems,
} from '@/lib/content-doctrine/article-factcheck-fixes';
import { formatWhen } from '@/lib/format-when';
import type { EditorialPillar } from '@/lib/reach/editorial-chain';
import {
  COLLAPSIBLE_MOTION,
  ITEM_META,
  ITEM_TITLE,
  MICRO_LABEL,
  SECTION_LABEL,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { ArticleReviewJob } from '@/services/data/ArticleJobService';
import {
  getDocumentOutput,
  setDocumentEditorialReviewDecision,
} from '@/services/data/DocumentsService';
import { useDocumentDialogStore } from '@/stores/useDocumentDialogStore';

/**
 * What happened to one editorial topic, in the dock.
 *
 * TWO TIERS, WHICH IS THE FOUNDER'S OWN INSTRUCTION (2026-09-06): _"The article
 * could be as simple right now as a right panel that pops out, matching the
 * other things, like when you click an event and it shows event details... When
 * we view the document in full, it should use the document viewer, which is that
 * immersive inset dialog."_ So: the dock answers *what is this and what has it
 * been through*, and reading the prose is a deliberate second step into the
 * Inspector. A 1,400-word article does not belong in a 400px rail.
 *
 * THE STEPS ARE THE POINT, and they are the answer to _"how do I know what
 * checks the article went through?"_. The competitor shows a 9-item checklist
 * with a tick per step. This shows the same shape and can say WHY each tick is
 * earned, because Mora records more: how many sources there were AND how many
 * were actually retrieved rather than merely cited by a search model, how many
 * brief attempts were rejected before one passed, and whether the brand voice
 * was resolved from the workspace or fell back to a default.
 *
 * Every line here is read back from the turn that produced the article — see
 * `useEditorialArticleTrail` for why `ai_messages.parts` is the only durable
 * copy. Nothing is asserted that was not recorded.
 */

/** A quote as the review shows it, so a refusal recorded against a passage is found beside that passage. */
let reviewRequestSequence = 0;
/** One id per press of Fix with AI / Edit passage, so a repeated press for the same finding is a new request. */
const nextReviewRequestId = (): number => {
  reviewRequestSequence += 1;
  return reviewRequestSequence;
};

const flatQuote = (quote: string): string =>
  quote
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

/** The refusals the server can give an address request, in words (the API returns the code). */
function addressRequestError(error: unknown): string {
  const code = error instanceof Error ? error.message : '';
  if (code === 'address_already_ran') return ADDRESS_REFUSAL_WORDS.already_ran;
  if (code === 'article_handed_off') return ADDRESS_REFUSAL_WORDS.handed_off;
  if (code === 'article_job_active') return 'Another run is still working on this article.';
  if (code === 'article_changed')
    return 'The article changed since this page loaded. Reload it and try again.';
  return code || 'Could not address the review notes.';
}

/** `claude-code` is the only author an import records today; anything else is shown as written. */
function importAuthorLabel(author: string): string {
  return author === 'claude-code' ? 'Claude Code' : author;
}

const VERIFIED_REWRITE_INSTRUCTION =
  'Research this topic again before writing. Fetch and read every source you cite, and cite only sources you have read. Tie every specific figure, statute and deadline to a source you read, and delete any claim no retrieved source supports. Keep the angle, audience and brand voice.';

export function EditorialTopicDockBody({
  topicId,
  variant = 'dock',
  focusFindingRef = null,
}: {
  topicId: string;
  variant?: 'dock' | 'document';
  /** Short ref of a critique finding to scroll to and highlight (the Slack note deep link). */
  focusFindingRef?: string | null;
}): JSX.Element {
  // Takes an id and resolves it, like every other Reach dock body. It reads
  // through `useEditorialMap`'s existing react-query cache rather than issuing
  // a second query for one row — the map is already loaded whenever this panel
  // can be opened, and the pillar/branch NAMES only exist after that hook's
  // resolution step (nothing stores a name on a topic).
  const { data: map } = useEditorialMap();
  const row =
    [...(map?.allTopics ?? []), ...(map?.orphanedTopics ?? [])].find((t) => t.id === topicId) ??
    null;
  const currentPillar = map?.pillars.find((p) => p.id === row?.pillarId) ?? null;
  const pillarName = currentPillar?.name ?? (row?.pillarId ? 'Older taxonomy' : 'Not filed');
  const branchName =
    map?.pillars.flatMap((p) => p.branches).find((b) => b.id === row?.branchId)?.name ?? null;
  const sourceArtifactId = row?.artifactId ?? null;
  const { activeKit } = useBrandKits();
  const { user } = useAuth();
  const articleJob = useArticleReviewJob(activeKit?.id ?? null, topicId, sourceArtifactId);
  const artifactId = sourceArtifactId ?? articleJob.job?.resultMessageId ?? null;
  const [improveOpen, setImproveOpen] = React.useState(false);
  const [improveMode, setImproveMode] = React.useState<'improve' | 'rewrite' | 'retry' | 'claims'>(
    'improve',
  );
  const [openingAlternate, setOpeningAlternate] = React.useState(false);
  const alternateScope = React.useRef({ active: true, request: 0 });
  React.useEffect(() => {
    setImproveOpen(false);
    setOpeningAlternate(false);
    const scope = { active: true, request: 0 };
    alternateScope.current = scope;
    return () => {
      scope.active = false;
    };
  }, [topicId, activeKit?.id]);
  const topic = {
    title: row?.topic ?? 'Untitled topic',
    pillarName,
    branchName,
    audience: row?.audience ?? null,
    reference: row?.reference ?? null,
  };

  const { data: trail, isPending } = useEditorialArticleTrail(artifactId);
  const linkedFindingKey = findLinkedFindingKey(
    trail?.editorialCritique?.report.findings ?? [],
    focusFindingRef,
  );
  const openDocument = useDocumentDialogStore((s) => s.open);
  const focusedFindingKey = useDocumentDialogStore((s) => s.focusedFindingKey);
  const focusFinding = useDocumentDialogStore((s) => s.focusFinding);
  const focusRequestId = useDocumentDialogStore((s) => s.focusRequestId);
  const queryClient = useQueryClient();
  const [openingRevisionId, setOpeningRevisionId] = React.useState<string | null>(null);
  const revisionScope = React.useRef({ active: true, request: 0 });
  React.useEffect(() => {
    setOpeningRevisionId(null);
    const scope = { active: true, request: 0 };
    revisionScope.current = scope;
    return () => {
      scope.active = false;
    };
  }, [topicId, activeKit?.id]);
  const [reviewDecisionPending, setReviewDecisionPending] = React.useState<string | null>(null);
  // Keep the dock render-safe when a legacy React Query cache entry (or a
  // mocked/partially persisted trail) predates review decisions. The article
  // remains reviewable; missing decisions simply mean nothing has been
  // marked reviewed yet.
  const reviewedFindingKeys = normalizeReviewedFindingKeys(
    trail?.editorialCritique?.reviewedFindingKeys,
  );
  // A finding the claim check has already answered with a verified quote is not an open decision.
  const decidedFindingKeySet = decidedFindingKeys(
    reviewedFindingKeys,
    trail?.editorialCritique?.claimCheckConfirmations,
    trail?.editorialCritique?.reviewerLimitKeys,
  );
  const pendingCritiqueFindings = trail?.editorialCritique
    ? trail.editorialCritique.report.findings.filter(
        (finding) => !decidedFindingKeySet.has(articleCritiqueFindingKey(finding)),
      )
    : [];
  const hasCurrentMechanicalIssue = Boolean(
    trail?.webReadiness?.isCurrent && !trail.webReadiness.ready,
  );
  // A flagged dimension carries no quotation, so it never reached
  // `pendingCritiqueFindings` and the summary below used to read "No unresolved
  // finding is recorded" while the critique panel simultaneously said
  // "1 need attention" — two judges of one state, and the reassuring one won.
  // A dimension flagged only because the reviewer could not see a source (every finding under it is a reviewer
  // limit) is a limitation of the review, not something to decide.
  const flaggedDimensionCount =
    trail?.editorialCritique?.report.dimensions.filter((dimension) => {
      if (dimension.assessment !== 'needs_attention') return false;
      const own = trail.editorialCritique!.report.findings.filter(
        (finding) => finding.dimension === dimension.dimension,
      );
      const limits = trail.editorialCritique!.reviewerLimitKeys;
      return !(
        own.length > 0 && own.every((finding) => limits?.has(articleCritiqueFindingKey(finding)))
      );
    }).length ?? 0;
  const reviewOutcomeMessage =
    pendingCritiqueFindings.length > 0
      ? `${pendingCritiqueFindings.length} editorial finding${pendingCritiqueFindings.length === 1 ? ' needs' : 's need'} a decision below. Draft checks are separate.`
      : hasCurrentMechanicalIssue
        ? 'Draft checks still have findings below.'
        : flaggedDimensionCount > 0
          ? `${flaggedDimensionCount} critique dimension${flaggedDimensionCount === 1 ? ' needs' : 's need'} attention. No passage was quoted, so read the assessment below and decide.`
          : trail?.editorialCritique?.isCurrent &&
              trail.editorialCritique.report.status !== 'reviewed'
            ? 'No unresolved finding is recorded. The automated critique has source limits.'
            : null;
  const reviewActionRequired = pendingCritiqueFindings.length > 0 || hasCurrentMechanicalIssue;
  const steps = buildTopicSteps(
    row,
    trail ?? null,
    articleJob.job,
    reviewActionRequired,
    reviewOutcomeMessage,
  );

  const setFindingReviewed = async (
    finding: ArticleCritiqueReport['findings'][number],
    reviewed: boolean,
  ) => {
    if (!user?.id || !artifactId || !trail?.documentId || !trail.editorialCritique?.isCurrent)
      return;
    const findingKey = articleCritiqueFindingKey(finding);
    setReviewDecisionPending(findingKey);
    try {
      const result = await setDocumentEditorialReviewDecision(
        artifactId,
        trail.documentId,
        user.id,
        trail.editorialCritique.report.contentHash,
        findingKey,
        reviewed,
      );
      if (!result.ok)
        throw new Error('error' in result ? result.error : 'Could not save the decision.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['editorial-article-trail', artifactId] }),
        queryClient.invalidateQueries({ queryKey: ['editorial-article-scores'] }),
      ]);
      toast.success(
        reviewed ? 'Editorial finding marked reviewed.' : 'Editorial finding returned to review.',
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save this review decision.');
    } finally {
      setReviewDecisionPending(null);
    }
  };

  const updateTopic = useUpdateEditorialTopic(activeKit?.id ?? null);
  /*
   * Sticky for the life of the panel rather than derived per save: once an edit
   * has diverged from what the client was sent, a SECOND edit that happens to
   * touch nothing must not clear the warning. The divergence is a property of
   * the row, not of the last keystroke.
   */
  const [changedSinceSent, setChangedSinceSent] = React.useState(false);

  const saveTopic = async (patch: {
    topic?: string;
    audience?: string;
    reference?: string;
    pillarId?: string;
    branchId?: string | null;
  }) => {
    if (!row) return;
    try {
      const result = await updateTopic.mutateAsync({ taskId: row.id, ...patch });
      if (result.wasSent) setChangedSinceSent(true);
    } catch (error) {
      // Surfaced, never swallowed. A silent failure here means the operator
      // believes they fixed a headline that a real client is still reading.
      toast.error(error instanceof Error ? error.message : 'Could not save that change.');
    }
  };

  const revisions = articleJob.job?.revisions ?? [];
  const openArticleRevision = (revision: NonNullable<ArticleReviewJob['revisions']>[number]) => {
    if (!user?.id || revision.isCurrent) return;
    const scope = revisionScope.current;
    const requestId = ++scope.request;
    setOpeningRevisionId(revision.messageId);
    void getDocumentOutput(revision.messageId, revision.documentId, user.id)
      .then((output) => {
        if (!scope.active || requestId !== scope.request) return;
        if (!output) throw new Error('This saved version could not be read.');
        openDocument({
          document: {
            id: output.id,
            messageId: output.messageId,
            title: `${topic.title} · ${versionLabel(revision.ordinal, revision.kind)}`,
            markdown: output.markdown,
            type: 'article',
            editorialTopicId: topicId,
            historicalNotice:
              revision.kind === 'address_before'
                ? 'The article as it was before its review notes were addressed. Not the current article.'
                : revision.ordinal < 0
                  ? 'The article as it was before this rewrite. Not the current article.'
                  : `${versionLabel(revision.ordinal, revision.kind)}, saved ${formatWhen(
                      revision.createdAt,
                    )}. Not the current article.`,
            promotable: true,
            currentVersion:
              trail?.markdown && artifactId
                ? {
                    id: trail.documentId,
                    messageId: artifactId,
                    title: trail.title || topic.title,
                    markdown: trail.markdown as string,
                    type: 'article',
                    editorialTopicId: topicId,
                  }
                : undefined,
          },
        });
      })
      .catch((error) =>
        toast.error(
          error instanceof Error ? error.message : 'This saved version could not be read.',
        ),
      )
      .finally(() => {
        if (revisionScope.current.active && requestId === revisionScope.current.request)
          setOpeningRevisionId(null);
      });
  };

  const actionBusy =
    articleJob.active ||
    articleJob.start.isPending ||
    articleJob.isPending ||
    Boolean(articleJob.error);
  const startDraft = () =>
    void articleJob.start
      .mutateAsync({ operation: 'draft', topicId, expectedArtifactId: null })
      .catch((error) =>
        toast.error(error instanceof Error ? error.message : 'Could not queue this article.'),
      );
  const recheck = recheckAction({
    canRead: Boolean(trail?.markdown && trail.documentId),
    active: articleJob.active,
    hasReport: Boolean(trail?.webReadiness),
    isCurrent: Boolean(trail?.webReadiness?.isCurrent),
    ready: Boolean(trail?.webReadiness?.ready),
  });
  const startRecheck = () => {
    if (!trail?.markdown || !trail.documentId) return;
    void articleJob.start
      .mutateAsync({
        operation: 'recheck',
        topicId,
        documentId: trail.documentId,
        markdown: trail.markdown,
      })
      .catch((error) =>
        toast.error(error instanceof Error ? error.message : 'Could not check the article.'),
      );
  };
  // A handed-off article is out of Mora's hands: the six ITC articles already with
  // the publisher must not be offered a check they cannot act on. Read from the
  // latest NON-CANCELLED publication row for this topic, at any content hash, and
  // withheld while that read is still loading so the action never flashes in.
  const publications = useArticlePublicationStates(artifactId ? [topicId] : []);
  const canCheckClaims = Boolean(
    trail?.markdown &&
    trail.documentId &&
    publications.isSuccess &&
    !publications.data.has(topicId),
  );
  const startFactCheck = () => {
    if (!trail?.markdown || !trail.documentId) return;
    void articleJob.start
      .mutateAsync({
        operation: 'factcheck',
        topicId,
        documentId: trail.documentId,
        markdown: trail.markdown,
      })
      .catch((error) =>
        toast.error(
          error instanceof Error ? error.message : 'Could not check the claims against sources.',
        ),
      );
  };
  const claimFixes = trail?.factCheck?.isCurrent ? factCheckFixItems(trail.factCheck.report) : [];
  const canFixClaims = canCheckClaims && claimFixes.length > 0;
  // ADDRESS REVIEW NOTES. Rewrites only the passages the open notes and the wrong-law claims quote, under
  // code-enforced guards, and saves the result as a new version (never a whole-article rewrite).
  const startAddress = (scope: 'all' | 'claims') => {
    if (!trail?.markdown || !trail.documentId) return;
    void articleJob.start
      .mutateAsync({
        operation: 'address',
        topicId,
        documentId: trail.documentId,
        markdown: trail.markdown,
        scope,
      })
      .catch((error) => toast.error(addressRequestError(error)));
  };
  const addressableCount = pendingCritiqueFindings.length + claimFixes.length;
  const addressPassRan = addressPassCoversCurrentRules(trail?.addressPass);
  const canAddress = canCheckClaims && addressableCount > 0 && !addressPassRan;
  // "Fix the claims" rewrites the whole article through the writer, which introduced new legal errors into 3
  // articles. When every claim it would fix is one sentence, the passage fixer does the same job without touching
  // anything else; anything bigger keeps the original path.
  const claimsGoToAddress =
    claimFixes.length > 0 &&
    !addressPassRan &&
    claimFixes.every((item) => isSingleSentence(item.sentence));
  const autoAddress = useEditorialAutoAddress(activeKit?.id ?? null);
  const wrongLawCount = claimFixes.filter((item) => item.priority === 'must_fix').length;
  const openImprove = (mode: 'improve' | 'rewrite' | 'retry' | 'claims') => {
    setImproveMode(mode);
    setImproveOpen(true);
  };

  // The one remedy for an article whose claims were never checked against a
  // source. Before this, the check that failed it said "write it again" and
  // "Write article" refuses any topic that already has an article.
  const sourcesRead = trail?.evidence
    ? trail.evidence.sourcesRetrieved
    : (trail?.sources.filter((source) => source.hasReceipt).length ?? 0);
  const needsVerifiedRewrite = Boolean(
    trail?.markdown &&
    trail.documentId &&
    ((trail.evidence
      ? trail.evidence.sourcesRetrieved === 0
      : trail.sources.length > 0 && sourcesRead === 0) ||
      trail.webReadiness?.findings.some(
        (finding) =>
          (finding.id === 'sources_were_retrieved' || finding.id === 'claims_found_in_sources') &&
          finding.severity === 'fail',
      )),
  );

  const lastJob = articleJob.job;
  const lastRunEnded = lastJob?.status === 'failed' || lastJob?.status === 'cancelled';
  const draftProducedNothing =
    lastJob?.operation === 'draft' && !artifactId && (lastRunEnded || lastJob.status === 'partial');
  // A retry is offered only where repeating the run could change the outcome.
  // A partial recheck is a finished review with limits, not a failure; a
  // partial revise that was not adopted has an alternate to read instead.
  const retry =
    !lastJob || articleJob.active || draftProducedNothing
      ? undefined
      : lastRunEnded && lastJob.operation === 'recheck' && trail?.markdown && trail.documentId
        ? { label: 'Check again', run: startRecheck }
        : lastRunEnded && lastJob.operation === 'factcheck' && canCheckClaims
          ? { label: 'Check claims again', run: startFactCheck }
          : lastRunEnded && lastJob.operation === 'revise' && trail?.markdown && trail.documentId
            ? { label: 'Try again', run: () => openImprove('retry') }
            : undefined;

  const improveCopy =
    improveMode === 'rewrite'
      ? {
          title: 'Rewrite with verified sources',
          description:
            'Mora researches this topic again and reads each source before writing a word. The rewrite replaces this article only if it introduces no new failed checks; otherwise it is kept as an alternate you can read and adopt. Either way, the article as it is now stays under Versions as “Before the rewrite”, so you can switch back.',
          submitLabel: 'Rewrite with verified sources',
          initialFeedback: VERIFIED_REWRITE_INSTRUCTION,
        }
      : improveMode === 'claims'
        ? {
            title: 'Fix the claims',
            description:
              'Creates a revised draft that rewrites each sentence the claim check found wrong. The instruction is built from the check: the sentence, what the statute says, and the statute text it read. The revision replaces this article only if it adds no failed checks; either way the current article stays under Versions. Check the claims again afterwards.',
            submitLabel: 'Fix the claims',
            initialFeedback: buildFactCheckFixFeedback(trail?.factCheck),
          }
        : improveMode === 'retry'
          ? {
              title: 'Try the improvement again',
              description:
                'Runs the same improvement with the instruction you gave last time. Change it if you want a different result.',
              submitLabel: 'Try again',
              initialFeedback: lastJob?.feedback ?? '',
            }
          : { initialFeedback: '' };

  // What needs the reader is listed first: a claim check that needs review, open notes, or draft
  // checks to fix sit above sections that passed. Within each group the order stays the same.
  const draftChecksSection =
    artifactId && !isPending ? (
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className={SECTION_LABEL}>Draft checks</p>
          <EditorialScore
            hasArticle
            job={articleJob.job ?? undefined}
            readiness={trail?.webReadiness ?? null}
          />
        </div>
        <p className={ITEM_META}>
          {articleJob.active
            ? articleJob.job?.operation === 'factcheck'
              ? 'A claim check is running. The draft checks below are unchanged.'
              : ['review', 'critique', 'validate', 'checks'].includes(
                    (articleJob.job?.currentStage ?? '').replace(/_\d+$/, ''),
                  )
                ? 'This run is checking the saved draft. Earlier results remain visible until the new check is saved.'
                : 'Draft checks will begin after the current writing stage.'
            : !trail?.webReadiness
              ? 'No check report is saved for this draft.'
              : !trail.webReadiness.isCurrent
                ? trail.webReadiness.staleReason ||
                  'This report no longer describes the current draft and checks.'
                : `${trail.webReadiness.applied} checks ran${trail.webReadiness.ready ? ' and passed' : ''}.${
                    trail.webReadiness.total > trail.webReadiness.applied
                      ? ` ${trail.webReadiness.total - trail.webReadiness.applied} conditional check${trail.webReadiness.total - trail.webReadiness.applied === 1 ? '' : 's'} did not apply to this article.`
                      : ''
                  } These checks cover page structure and citations, not writing quality or client approval.`}
        </p>
        {recheck.show ? (
          <div className="space-y-1">
            <Button
              variant={recheck.emphasis === 'primary' ? 'outline' : 'flat'}
              size="sm"
              className={recheck.emphasis === 'secondary' ? '-ml-3' : undefined}
              disabled={
                articleJob.active ||
                articleJob.start.isPending ||
                articleJob.isPending ||
                Boolean(articleJob.error)
              }
              aria-describedby={recheck.note ? `recheck-note-${topicId}` : undefined}
              onClick={startRecheck}
            >
              {articleJob.start.isPending ? 'Starting…' : recheck.label}
            </Button>
            {recheck.note ? (
              <p id={`recheck-note-${topicId}`} className={ITEM_META}>
                {recheck.note}
              </p>
            ) : null}
          </div>
        ) : !trail?.markdown || !trail.documentId ? (
          <p className={ITEM_META}>Checking is available once the saved article can be read.</p>
        ) : null}
        {canCheckClaims && !articleJob.active ? (
          <div className="space-y-1">
            <Button
              variant="outline"
              size="sm"
              disabled={actionBusy}
              onClick={startFactCheck}
              aria-describedby={`claim-check-note-${topicId}`}
            >
              {articleJob.start.isPending
                ? 'Starting…'
                : trail?.factCheck
                  ? 'Check claims again'
                  : 'Check claims against sources'}
            </Button>
            <p id={`claim-check-note-${topicId}`} className={ITEM_META}>
              Reads the statutes this article cites on the Texas Legislature&apos;s site and
              compares each claim with the law. It never edits the article.
            </p>
          </div>
        ) : null}
        {canFixClaims && !articleJob.active ? (
          <div className="space-y-1">
            <Button
              variant="neutral"
              size="sm"
              disabled={actionBusy}
              onClick={() => (claimsGoToAddress ? startAddress('claims') : openImprove('claims'))}
              aria-describedby={`claim-fix-note-${topicId}`}
            >
              Fix the claims
            </Button>
            <p id={`claim-fix-note-${topicId}`} className={ITEM_META}>
              {wrongLawCount > 0
                ? `${wrongLawCount} claim${wrongLawCount === 1 ? '' : 's'} the check found wrong against the law${
                    claimFixes.length > wrongLawCount
                      ? `, and ${claimFixes.length - wrongLawCount} the cited provision does not state`
                      : ''
                  }.`
                : `${claimFixes.length} claim${claimFixes.length === 1 ? '' : 's'} the cited provision does not state.`}{' '}
              {claimsGoToAddress
                ? 'Rewrites only the sentence each one names, from the statute text the check read. Nothing else in the article changes.'
                : 'Starts a revision that rewrites each one from the statute text the check read.'}
            </p>
          </div>
        ) : null}
        {trail?.webReadiness ? (
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="flat" size="sm" className="group -ml-2">
                <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
                Check details ·{' '}
                {trail.webReadiness.checks.filter((check) => check.state === 'passed').length}{' '}
                passed
                {trail.webReadiness.checks.some((check) => check.state === 'not_applied')
                  ? ` · ${trail.webReadiness.checks.filter((check) => check.state === 'not_applied').length} not applicable`
                  : trail.webReadiness.findings.length
                    ? ` · ${trail.webReadiness.findings.length} finding${trail.webReadiness.findings.length === 1 ? '' : 's'}`
                    : ''}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className={cn(COLLAPSIBLE_MOTION, 'space-y-3 pt-2')}>
              {!trail.webReadiness.isCurrent ? (
                <p className={ITEM_META}>
                  Historical results below. They do not validate the current draft.
                </p>
              ) : null}
              {trail.webReadiness.findings.length ? (
                <ul className="space-y-2">
                  {trail.webReadiness.findings.map((finding) => (
                    <WebReadinessFindingRow key={finding.id + finding.detail} finding={finding} />
                  ))}
                </ul>
              ) : null}
              <ul className="space-y-2">
                {trail.webReadiness.checks.map((check) => (
                  <li
                    key={check.id}
                    className="flex items-start justify-between gap-3 text-caption"
                  >
                    <span>{checkLabel(check.id)}</span>
                    <span
                      className={cn(
                        'shrink-0',
                        check.state === 'passed'
                          ? 'text-success'
                          : check.state === 'fail'
                            ? 'text-destructive'
                            : check.state === 'warn'
                              ? 'text-warning'
                              : 'text-muted-foreground',
                      )}
                    >
                      {check.state === 'passed'
                        ? 'Passed'
                        : check.state === 'fail'
                          ? 'Failed'
                          : check.state === 'warn'
                            ? 'Warning'
                            : check.state === 'not_applied'
                              ? 'Not applied'
                              : 'Not recorded'}
                    </span>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
        {trail?.editingPass ? (
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="flat" size="sm" className="group -ml-2">
                <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
                Language diagnostics · {trail.editingPass.findings.length}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className={cn(COLLAPSIBLE_MOTION, 'space-y-2 pt-2')}>
              <p className={ITEM_META}>
                {trail.editingPass.isHistorical ? 'Historical diagnostics from generation. ' : ''}
                Flags repetitive and generic language; this is not an editorial quality score.
              </p>
              {trail.editingPass.findings.length ? (
                <ul className="space-y-2">
                  {trail.editingPass.findings.map((finding, index) => (
                    <li key={index} className="text-caption text-muted-foreground">
                      {finding}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={ITEM_META}>No language patterns were flagged in the saved pass.</p>
              )}
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </section>
    ) : null;
  const factCheckSection =
    artifactId && !isPending && trail?.factCheck ? (
      <EditorialFactCheck factCheck={trail.factCheck} />
    ) : null;
  const critiqueSection = trail?.editorialCritique ? (
    <EditorialCritique
      report={trail.editorialCritique.report}
      isCurrent={trail.editorialCritique.isCurrent}
      reviewedFindingKeys={reviewedFindingKeys}
      confirmations={trail.editorialCritique.claimCheckConfirmations}
      reviewerLimitKeys={trail.editorialCritique.reviewerLimitKeys}
      pendingFindingKey={reviewDecisionPending}
      onSetFindingReviewed={setFindingReviewed}
      refreshing={articleJob.active || articleJob.start.isPending}
      onRefreshReview={() =>
        trail.markdown && trail.documentId
          ? articleJob.start.mutateAsync({
              operation: 'recheck',
              topicId,
              documentId: trail.documentId,
              markdown: trail.markdown,
              refreshCritique: true,
            })
          : Promise.resolve()
      }
      address={{
        available: canAddress,
        blockedReason: addressPassRan ? ADDRESS_REFUSAL_WORDS.already_ran : null,
        openCount: addressableCount,
        onAddress: () => startAddress('all'),
        busy: actionBusy,
        statusByQuote: new Map(
          [...(trail.addressPass?.rejected ?? []), ...(trail.addressed?.rejected ?? [])].map(
            (entry: AddressRejectionRecord) => [flatQuote(entry.quote), entry] as const,
          ),
        ),
        auto: {
          mode: autoAddress.mode,
          pending: autoAddress.isPending || autoAddress.update.isPending,
          onChange: (next) =>
            autoAddress.update.mutate(next, {
              onError: (error) =>
                toast.error(
                  error instanceof Error ? error.message : 'Could not save this setting.',
                ),
            }),
        },
      }}
      {...(variant === 'document'
        ? { focusedFindingKey, focusRequestId, onFocusFinding: focusFinding }
        : {})}
      tagNote={{ topicId }}
      linkedFindingKey={linkedFindingKey}
      onOpenFinding={(finding, withAi) =>
        trail.markdown && trail.documentId
          ? openDocument({
              document: {
                id: trail.documentId,
                title: trail.title || topic.title,
                markdown: trail.markdown,
                type: 'article',
                messageId: artifactId,
                editorialTopicId: topicId,
                reviewAction: {
                  quote: finding.quote,
                  ...(withAi ? { instruction: finding.suggestion } : {}),
                  requestId: nextReviewRequestId(),
                },
              },
            })
          : undefined
      }
    />
  ) : null;
  const reviewSections = [
    {
      key: 'draft-checks',
      node: draftChecksSection,
      attention: Boolean(
        trail?.webReadiness && (!trail.webReadiness.isCurrent || !trail.webReadiness.ready),
      ),
    },
    {
      key: 'claims',
      node: factCheckSection,
      attention: Boolean(
        trail?.factCheck &&
        (!trail.factCheck.isCurrent ||
          trail.factCheck.report.status === 'needs_review' ||
          trail.factCheck.report.status === 'failed'),
      ),
    },
    {
      key: 'critique',
      node: critiqueSection,
      // A flagged dimension carries no quotation, so open findings alone would miss it (see
      // `flaggedDimensionCount`).
      attention: pendingCritiqueFindings.length > 0 || flaggedDimensionCount > 0,
    },
  ];
  const orderedReviewSections = [
    ...reviewSections.filter((section) => section.attention),
    ...reviewSections.filter((section) => !section.attention),
  ];

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden p-4">
      {variant === 'dock' ? (
        <header className="space-y-1">
          {/*
           * 🔴 EDITABLE, BECAUSE IT WAS NOT AND THAT WAS THE GAP. Founder,
           * 2026-09-07: *"I see that there are proposed articles there, but I
           * don't really know how I edit those. Is there somewhere to even edit
           * those manually myself, or do I have to export it?"* There was not:
           * `useCreateEditorialTopic` shipped with no update sibling, so a topic
           * could be added and written about but never corrected — and these
           * headlines go to a real client's stakeholder.
           *
           * `InlineFieldEdit` is the repo's canonical in-place editor rather than
           * a fifth hand-rolled one; it already owns the pencil affordance, the
           * pending spinner and the escape-to-cancel behaviour.
           */}
          <InlineFieldEdit
            value={topic.title}
            ariaLabel="this topic"
            multiline
            rows={2}
            displayClassName={cn(ITEM_TITLE, 'text-sm leading-snug')}
            onSave={(next) => void saveTopic({ topic: next })}
          />
          <p className={ITEM_META}>
            {topic.pillarName}
            {topic.branchName ? ` · ${topic.branchName}` : null}
          </p>
          {/*
           * 🔴 THE MARKER IS NOT OPTIONAL — THE EDIT WOULD BE A TRAP WITHOUT IT.
           * `approval_items.snapshot` is frozen at send time, deliberately: a
           * verdict must attach to the text the person actually read, which is
           * why `item_snapshot_hash` is NOT NULL. So editing a topic already sent
           * leaves the client looking at, and approving, the old wording. That is
           * correct behaviour and invisible unless it is said out loud.
           */}
          {changedSinceSent ? (
            <p className="pt-1 text-micro leading-relaxed text-warning">
              Changed since you sent it. Your client still sees the version they were sent — send it
              again for them to see this one.
            </p>
          ) : null}
        </header>
      ) : (
        // The article's title already heads the page beside this rail.
        <header>
          <p className={SECTION_LABEL}>Article review</p>
        </header>
      )}

      {!currentPillar && row && map ? (
        <RefileTopic
          topicId={row.id}
          pillars={map.pillars}
          onSave={(pillarId, branchId) => saveTopic({ pillarId, branchId })}
          saving={updateTopic.isPending}
        />
      ) : null}

      {trail?.importProvenance ? (
        <div className="space-y-0.5">
          <p className={ITEM_TITLE}>Imported — written outside Mora&apos;s writer</p>
          <p className={ITEM_META}>
            {importAuthorLabel(trail.importProvenance.author)}
            {trail.importProvenance.importedAt
              ? `, ${formatWhen(trail.importProvenance.importedAt)}`
              : ''}
            {trail.importProvenance.note ? `. ${trail.importProvenance.note}` : ''}
          </p>
        </div>
      ) : null}

      {variant === 'dock' && trail?.markdown ? (
        <Button
          variant="neutral"
          size="sm"
          className="w-full"
          onClick={() =>
            openDocument({
              document: {
                id: trail.documentId,
                title: trail.title || topic.title,
                markdown: trail.markdown as string,
                type: 'article',
                messageId: artifactId,
                editorialTopicId: topicId,
              },
            })
          }
        >
          <ArrowSquareOutIcon weight="regular" />
          Read article
        </Button>
      ) : variant === 'dock' && row && currentPillar && !artifactId && !articleJob.active ? (
        <Button
          variant="neutral"
          size="sm"
          className="w-full"
          disabled={actionBusy}
          onClick={startDraft}
        >
          <PencilSimpleLineIcon weight="regular" />
          {articleJob.start.isPending
            ? 'Starting…'
            : draftProducedNothing
              ? 'Try writing again'
              : 'Write article'}
        </Button>
      ) : null}
      {variant === 'dock' &&
      draftProducedNothing &&
      !articleJob.active &&
      !lastJob?.errorMessage?.includes('evidence budget') ? (
        // The research gate refusing to draft is the gate working, not a
        // crash — but a person still has to know what to do next. The
        // evidence-budget case is said once, by the run's own progress card
        // (`articleJobOutcomeMessage`), which also covers rewrites.
        <p className={ITEM_META}>
          No article was written. Any research from the last attempt is kept.
        </p>
      ) : null}

      {variant === 'dock' && trail?.markdown && trail.documentId ? (
        <Button
          variant={needsVerifiedRewrite ? 'neutral' : 'outline'}
          size="sm"
          className="w-full"
          disabled={actionBusy}
          onClick={() => openImprove(needsVerifiedRewrite ? 'rewrite' : 'improve')}
        >
          {needsVerifiedRewrite ? 'Rewrite with verified sources' : 'Improve article'}
        </Button>
      ) : null}
      {variant === 'dock' && trail?.markdown && publications.isSuccess ? (
        <ArticlePromoteActions
          topicId={topicId}
          title={trail.title || topic.title}
          publication={publications.data.get(topicId)}
          wrongLawCount={wrongLawCount}
          needsVerifiedRewrite={needsVerifiedRewrite}
        />
      ) : null}
      {trail?.markdown && trail.documentId ? (
        <ImproveArticleDialog
          key={topicId}
          open={improveOpen}
          onOpenChange={setImproveOpen}
          pending={articleJob.start.isPending}
          {...improveCopy}
          onImprove={(feedback) =>
            articleJob.start.mutateAsync({
              operation: 'revise',
              topicId,
              documentId: trail.documentId!,
              markdown: trail.markdown!,
              feedback,
            })
          }
        />
      ) : null}
      {articleJob.job ? (
        <ArticleJobProgress
          job={articleJob.job}
          stopping={articleJob.stop.isPending}
          onStop={() => articleJob.stop.mutateAsync({ jobId: articleJob.job!.id, topicId })}
          partialMessage={
            articleJob.job.operation === 'factcheck'
              ? (articleJob.job.errorMessage ?? null)
              : reviewOutcomeMessage
          }
          actionRequired={reviewActionRequired}
          onRetry={retry && !actionBusy ? retry.run : undefined}
          retryLabel={retry?.label}
          retrying={articleJob.start.isPending}
        />
      ) : null}
      {articleJob.error ? (
        <p role="alert" className="text-caption text-warning">
          Article progress could not be loaded. Reopen this topic to try again.
        </p>
      ) : null}
      {articleJob.job?.latestMessageId &&
      articleJob.job.latestMessageId !== artifactId &&
      articleJob.job.latestDocumentId ? (
        <Button
          variant="outline"
          size="sm"
          disabled={openingAlternate}
          onClick={() => {
            if (!user?.id || !articleJob.job?.latestMessageId || !articleJob.job.latestDocumentId)
              return;
            const scope = alternateScope.current;
            const requestId = ++scope.request;
            setOpeningAlternate(true);
            void getDocumentOutput(
              articleJob.job.latestMessageId,
              articleJob.job.latestDocumentId,
              user.id,
            )
              .then((alternate) => {
                if (!scope.active || requestId !== scope.request) return;
                if (!alternate) throw new Error('The alternate revision could not be read.');
                openDocument({
                  document: {
                    id: alternate.id,
                    messageId: alternate.messageId,
                    title: `${topic.title} · Alternate revision`,
                    markdown: alternate.markdown,
                    type: 'article',
                    editorialTopicId: topicId,
                    historicalNotice: articleJob.job?.errorMessage?.includes(
                      'new objective failures',
                    )
                      ? 'A rewrite that was kept aside because it failed checks the current article passes. Not the current article.'
                      : 'A rewrite that was not adopted automatically. Not the current article.',
                    promotable: true,
                    currentVersion:
                      trail?.markdown && artifactId
                        ? {
                            id: trail.documentId,
                            messageId: artifactId,
                            title: trail.title || topic.title,
                            markdown: trail.markdown as string,
                            type: 'article',
                            editorialTopicId: topicId,
                          }
                        : undefined,
                  },
                });
              })
              .catch((error) =>
                toast.error(
                  error instanceof Error
                    ? error.message
                    : 'The alternate revision could not be read.',
                ),
              )
              .finally(() => {
                if (scope.active && requestId === scope.request) setOpeningAlternate(false);
              });
          }}
        >
          {openingAlternate ? 'Opening revision…' : 'Read alternate revision'}
        </Button>
      ) : null}

      {orderedReviewSections.map((section) => (
        <React.Fragment key={section.key}>{section.node}</React.Fragment>
      ))}

      {trail?.addressed || trail?.addressPass ? (
        <EditorialAddressed
          addressed={trail.addressed}
          pass={trail.addressPass}
          now={{
            openNotes: trail.editorialCritique?.isCurrent ? pendingCritiqueFindings.length : null,
            wrongLaw: trail.factCheck?.isCurrent
              ? claimFixes.filter((item) => item.priority === 'must_fix').length
              : null,
            counts:
              trail.editorialCritique?.isCurrent && trail.addressed
                ? addressNoteCounts({
                    before: trail.addressed.before.openNotes,
                    open: pendingCritiqueFindings,
                    scoped: readScopedReview(trail.editorialCritique.report),
                    recorded: [...trail.addressed.edits, ...trail.addressed.rejected].filter(
                      (entry) => entry.kind === 'critique',
                    ),
                  })
                : null,
            stillOpen: trail.editorialCritique?.isCurrent
              ? stillOpenAfterAddress(trail.addressed, [
                  ...pendingCritiqueFindings.map((finding) => finding.quote),
                  ...claimFixes.map((item) => item.sentence),
                ])
              : null,
          }}
        />
      ) : null}

      {revisions.length > 1 ? (
        <section className="space-y-2">
          <p className={SECTION_LABEL}>Versions</p>
          <ul className="space-y-1">
            {[...revisions]
              .sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() ||
                  b.ordinal - a.ordinal,
              )
              .map((revision) => (
                <li key={revision.messageId}>
                  <button
                    type="button"
                    disabled={revision.isCurrent || openingRevisionId === revision.messageId}
                    onClick={() => openArticleRevision(revision)}
                    className={cn(
                      'flex min-h-8 w-full items-center gap-2 rounded-md px-2 text-left',
                      revision.isCurrent
                        ? 'cursor-default'
                        : 'hover:bg-muted/60 disabled:cursor-wait disabled:opacity-70',
                    )}
                  >
                    <span className={cn(ITEM_TITLE, 'shrink-0')}>
                      {versionLabel(revision.ordinal, revision.kind)}
                    </span>
                    <span className={cn(ITEM_META, 'min-w-0 flex-1 truncate')}>
                      {revision.kind === 'address'
                        ? `${revision.edits ?? 0} edit${revision.edits === 1 ? '' : 's'} · ${formatWhen(revision.createdAt)}`
                        : revision.ordinal < 0
                          ? `replaced ${formatWhen(revision.createdAt)}`
                          : formatWhen(revision.createdAt)}
                    </span>
                    {revision.isCurrent ? (
                      <StatusBadge tone="success" fill="outline" className="shrink-0">
                        Current
                      </StatusBadge>
                    ) : (
                      <span className={cn(ITEM_META, 'shrink-0')}>
                        {openingRevisionId === revision.messageId ? 'Opening…' : 'Read'}
                      </span>
                    )}
                  </button>
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      <section className="space-y-1">
        <p className={SECTION_LABEL}>Written for</p>
        <InlineFieldEdit
          value={topic.audience ?? ''}
          ariaLabel="who this is written for"
          placeholder="Add who this is for"
          multiline
          rows={2}
          displayClassName="text-caption text-muted-foreground"
          onSave={(next) => void saveTopic({ audience: next })}
        />
      </section>

      <section className="space-y-1">
        {/* WHERE THIS TOPIC CAME FROM. `project_tasks.summary` — the sheet's
            "Reference / inspiration" column. The panel was holding this and
            not showing it, which is half of the founder's question "where do
            these articles even come from?". */}
        <p className={SECTION_LABEL}>Proposed from</p>
        <InlineFieldEdit
          value={topic.reference ?? ''}
          ariaLabel="where this topic came from"
          placeholder="Add where this came from"
          multiline
          rows={2}
          displayClassName="text-caption text-muted-foreground"
          onSave={(next) => void saveTopic({ reference: next })}
        />
      </section>

      {isPending && artifactId ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-4 w-48" />
        </div>
      ) : (
        <>
          {/*
           * 🔴 THE LADDER RENDERS FOR EVERY TOPIC, WRITTEN OR NOT.
           *
           * This used to short-circuit on `!artifactId` to a single line —
           * "Nothing has been written for this topic yet." — which was true and
           * useless, and it was what 30 of 32 topics showed. Founder, 2026-09-07:
           * _"Whenever I click into the articles, it just keeps saying 'Nothing
           * has been written'... It should show the process that they're going
           * through during it."_
           *
           * He is right, and the mistake was showing a TERMINAL STATE where the
           * surface's whole job is showing a PROCESS. A proposed topic has a
           * real answer to "what has happened and what happens next" — it is
           * just that most of the answer is "not yet". The competitor's
           * equivalent reads "Workflow Progress 4 / 9 completed (44%)" and shows
           * the five it has NOT done as empty circles; showing only the finished
           * ones would make an unstarted piece look broken rather than queued.
           */}
          <section className="space-y-2" hidden={articleJob.active}>
            <p className={SECTION_LABEL}>What this went through</p>
            <ol className="space-y-0">
              {steps.map((step, index) => (
                <StepRow key={step.id} step={step} isLast={index === steps.length - 1} />
              ))}
            </ol>
          </section>
        </>
      )}

      {trail ? (
        <>
          {!trail.sources.length && !trail.evidence && !trail.writerLedger && trail.markdown ? (
            <section className="space-y-1">
              <p className={SECTION_LABEL}>Evidence</p>
              <p className={ITEM_META}>
                No record of which sources this article&apos;s writer read. Recheck the article to
                confirm its citations and figures against sources Mora actually fetched.
              </p>
            </section>
          ) : null}
          {!trail.sources.length && !trail.evidence && trail.writerLedger ? (
            <section className="space-y-1">
              <p className={SECTION_LABEL}>Evidence</p>
              <p className={ITEM_META}>
                {`The writer tied ${trail.writerLedger.claims} sentence${trail.writerLedger.claims === 1 ? '' : 's'} to a page and a supporting quote, across ${trail.writerLedger.sources} page${trail.writerLedger.sources === 1 ? '' : 's'}.`}
                {(() => {
                  const ledgerClaims = trail.factCheck?.isCurrent
                    ? trail.factCheck.report.claims.filter((claim) => claim.ledger)
                    : [];
                  return ledgerClaims.length
                    ? ` ${ledgerClaims.filter((claim) => claim.ledger?.verified).length} of ${ledgerClaims.length} checked quotes were found word for word in the page Mora fetched.`
                    : ' Mora checks each quote against the page it fetches; a quote it cannot find counts for nothing.';
                })()}
              </p>
            </section>
          ) : null}
          {trail.sources.length || trail.evidence ? (
            <section className="space-y-2">
              <p className={SECTION_LABEL}>Evidence</p>
              {(() => {
                const read = trail.evidence
                  ? trail.evidence.sourcesRetrieved
                  : trail.sources.filter((source) => source.hasReceipt).length;
                const relied = trail.evidence
                  ? trail.evidence.sourcesSupplied
                  : trail.sources.length;
                // Zero read is the single most important fact in this panel —
                // every citation below was named, never opened. It was grey
                // meta text, indistinguishable from "2 of 2".
                return (
                  <p className={cn(ITEM_META, read === 0 && 'text-destructive')}>
                    {read === 0
                      ? `Mora did not read ${relied > 0 ? `any of the ${relied} source${relied === 1 ? '' : 's'} this article cites` : 'any source for this article'}. Its claims rest on the writing model’s memory — verify each one before publishing.`
                      : `${read} of ${relied} sources were fetched and read for this article.`}
                  </p>
                );
              })()}
              {variant === 'document' && needsVerifiedRewrite ? (
                <Button
                  variant="neutral"
                  size="sm"
                  disabled={actionBusy}
                  onClick={() => openImprove('rewrite')}
                >
                  Rewrite with verified sources
                </Button>
              ) : null}
              <ul className="space-y-1.5">
                {trail.sources.map((source) => (
                  /**
                   * 🔴 THE TITLE WRAPS. It was `truncate` on one line, and in a
                   * ~300px rail that clipped every source mid-word — measured:
                   * both entries reported `scrollWidth > clientWidth` at 272px,
                   * rendering as "Texas Comptroller of Public Accounts - Da…"
                   * and "Texas Tax Code Section 151.359 - Propert…". Founder,
                   * 2026-09-07: _"I can't even see the two sources where it
                   * says evidence gathered. I can't even see what that is."_
                   *
                   * Naming the evidence IS this section. A source you cannot
                   * read is the same defect one layer up from the one the
                   * whole doctrine is about — a citation that cannot be
                   * checked. Two lines and a badge above it costs a few
                   * pixels of height and returns the entire point.
                   */
                  <li key={source.url} className="space-y-0.5">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="block text-caption leading-snug text-foreground/90 underline decoration-border underline-offset-2 hover:decoration-foreground"
                      title={source.url}
                    >
                      {source.title}
                    </a>
                    <p className={ITEM_META} title={source.url}>
                      {source.hasReceipt
                        ? `Retrieved from ${hostOf(source.url)}`
                        : `Cited from ${hostOf(source.url)}`}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {trail.webMetadata ? (
            <section className="space-y-2">
              <p className={SECTION_LABEL}>Web metadata</p>

              {trail.webMetadata?.metaTitle ? (
                <MetadataField label="Title tag" value={trail.webMetadata.metaTitle} />
              ) : null}
              {trail.webMetadata?.metaDescription ? (
                <MetadataField label="Meta description" value={trail.webMetadata.metaDescription} />
              ) : null}
              {trail.webMetadata?.slug ? (
                <MetadataField label="Slug" value={`/${trail.webMetadata.slug}`} />
              ) : null}
            </section>
          ) : null}

          {trail.claimsToAvoid.length ? (
            <section className="space-y-2">
              <p className={SECTION_LABEL}>Claims this piece avoids</p>
              <ul className="space-y-1">
                {trail.claimsToAvoid.map((claim) => (
                  <li key={claim} className="text-caption text-muted-foreground">
                    {claim}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : artifactId && !isPending ? (
        // ONLY when an artifact id exists. Without this guard the sentence fired
        // on all 27 unwritten topics and told the reader an article "was filed"
        // that never existed — a false statement, and a worse one than the blank
        // panel it replaced, because it describes a failure rather than a state.
        // And only once the read has SETTLED: while it is in flight `trail` is
        // undefined too, so this failure sentence showed for the seconds every
        // article took to load, under the skeleton (seen on Percent Good,
        // handoff 2026-10-15 §3.3).
        <p className="text-caption text-muted-foreground">
          This article was filed but its record could not be read.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The whole ladder, for ANY topic — written or not.
 *
 * Derived from the row first and ENRICHED by the trail, rather than read off
 * the trail alone. That ordering is the fix: the trail only exists once an
 * article has been filed, so deriving from it made 30 of 32 topics render a
 * single "nothing written yet" line where the surface's job is to show the
 * process. A proposed topic has a real answer to "what has happened and what
 * happens next"; most of it is just "not yet", and "not yet" is information.
 *
 * Nothing here is asserted that was not recorded. A step is `unknown` — not
 * `pending` — when Mora genuinely has no signal, because "we have not done this"
 * and "we cannot see whether this happened" are different facts and only one of
 * them is a to-do.
 */
function buildTopicSteps(
  row: { status: string | null; hasArticle: boolean; reference: string | null } | null,
  trail: { steps: ArticleStep[] } | null,
  job: ArticleReviewJob | null,
  reviewActionRequired = false,
  reviewOutcomeMessage: string | null = null,
): ArticleStep[] {
  const proposed: ArticleStep = {
    id: 'proposed',
    label: 'Proposed under a pillar',
    state: 'done',
    detail: row?.reference ? `From ${row.reference}` : undefined,
  };

  // When the article exists, the trail carries the real, measured steps.
  if (trail?.steps.length) {
    const execution =
      job?.status === 'failed'
        ? [
            {
              id: 'latest-run',
              label: 'Latest run failed',
              state: 'failed' as const,
              detail: job.errorMessage || 'The saved draft remains available to review or improve.',
            },
          ]
        : job?.status === 'partial' && job.operation === 'factcheck'
          ? [
              {
                id: 'latest-run',
                label: 'Claim check out of date',
                state: 'pending' as ArticleStepState,
                detail:
                  job.errorMessage || 'The article changed while its claims were being checked.',
              },
            ]
          : job?.status === 'partial'
            ? [
                {
                  id: 'latest-run',
                  label: reviewActionRequired
                    ? 'Editorial decision needed'
                    : 'Review limits recorded',
                  state: (reviewActionRequired ? 'pending' : 'done') as ArticleStepState,
                  detail:
                    job.errorMessage ||
                    reviewOutcomeMessage ||
                    'The automated review completed with limitations recorded.',
                },
              ]
            : [];
    return [proposed, ...trail.steps, ...execution];
  }

  // When it does not, name the same path ahead so the reader can see where
  // this sits rather than being told only what is absent.
  return [
    proposed,
    { id: 'research', label: 'Evidence gathered', state: row?.hasArticle ? 'unknown' : 'pending' },
    {
      id: 'brief',
      label: 'Brief checked against the evidence',
      state: row?.hasArticle ? 'unknown' : 'pending',
    },
    {
      id: 'written',
      label: 'Article written',
      state: row?.hasArticle ? 'done' : 'pending',
      detail: row?.hasArticle
        ? 'Article filed; generation history is unavailable.'
        : 'Choose Write article to start',
    },
  ];
}

export function EditorialCritique({
  report,
  isCurrent,
  reviewedFindingKeys,
  confirmations,
  reviewerLimitKeys,
  pendingFindingKey,
  onSetFindingReviewed,
  onOpenFinding,
  onRefreshReview,
  refreshing,
  address,
  focusedFindingKey,
  focusRequestId = 0,
  onFocusFinding,
  tagNote,
  linkedFindingKey = null,
}: {
  report: ArticleCritiqueReport;
  isCurrent: boolean;
  reviewedFindingKeys: ReadonlySet<string>;
  /** Findings the claim check already answered with a verified quote; they are not open decisions. */
  confirmations?: ReadonlyMap<string, ClaimCheckConfirmation>;
  /** Findings that only say the reviewer could not see the source text. Listed as limits, never as decisions. */
  reviewerLimitKeys?: ReadonlySet<string>;
  /** "Address review notes": the passage-scoped automatic fix, its per-note status and the workspace setting. */
  address?: {
    available: boolean;
    /** Why the button is not offered, when there is a reason to say. */
    blockedReason: string | null;
    openCount: number;
    onAddress: () => void;
    busy: boolean;
    /** Why a note was not addressed, by the note's quote as the review shows it. */
    statusByQuote: ReadonlyMap<string, AddressRefusalView>;
    auto: { mode: AutoAddressMode; pending: boolean; onChange: (next: AutoAddressMode) => void };
  };
  pendingFindingKey: string | null;
  onSetFindingReviewed: (
    finding: ArticleCritiqueReport['findings'][number],
    reviewed: boolean,
  ) => Promise<void>;
  onOpenFinding: (finding: ArticleCritiqueReport['findings'][number], withAi: boolean) => void;
  onRefreshReview: () => Promise<unknown>;
  refreshing: boolean;
  /**
   * Document variant only: pressing a note's lead (dimension, reason and quote) points the reader at
   * its passage in the article. The dock variant has no article beside it, so it leaves these out.
   */
  focusedFindingKey?: string | null;
  /**
   * Bumped by every focus request (a card, a press on its passage in the article, the header's
   * "Review next note"), so the rail opens its findings and brings the focused card into view.
   */
  focusRequestId?: number;
  onFocusFinding?: (findingKey: string) => void;
  /** "Tag in Slack" on each open note: sends the note to a teammate. Left out, no Tag button. */
  tagNote?: { topicId: string };
  /**
   * The finding a deep link points at (`/reach/editorial?topic=&finding=`): scrolled into view
   * once and highlighted like a focused finding. Null when no link names one.
   */
  linkedFindingKey?: string | null;
}): JSX.Element {
  const linkedFindingRef = React.useRef<HTMLDivElement | null>(null);
  const scrolledToLinkedKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!linkedFindingKey || scrolledToLinkedKey.current === linkedFindingKey) return;
    const node = linkedFindingRef.current;
    if (!node) return;
    scrolledToLinkedKey.current = linkedFindingKey;
    node.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [linkedFindingKey]);
  const assessed = report.dimensions.filter(
    (dimension) => dimension.assessment !== 'not_assessed',
  ).length;
  const flaggedDimensions = report.dimensions.filter(
    (dimension) => dimension.assessment === 'needs_attention',
  );
  const needsAttention = flaggedDimensions.length;
  const limitKeys = reviewerLimitKeys ?? new Set<string>();
  const { actionable: shownFindings, limits: limitFindings } = partitionCritiqueFindings(report);
  const decidedKeys = decidedFindingKeys(reviewedFindingKeys, confirmations, limitKeys);
  const openFindings = shownFindings.filter(
    (finding) => !decidedKeys.has(articleCritiqueFindingKey(finding)),
  ).length;
  const confirmedCount = shownFindings.filter((finding) => {
    const key = articleCritiqueFindingKey(finding);
    return !reviewedFindingKeys.has(key) && Boolean(confirmations?.has(key));
  }).length;
  const [findingsOpen, setFindingsOpen] = React.useState(
    shownFindings.length > 0 || flaggedDimensions.length > 0,
  );
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const focusedIsShown =
    Boolean(focusedFindingKey) &&
    shownFindings.some((finding) => articleCritiqueFindingKey(finding) === focusedFindingKey);
  React.useEffect(() => {
    if (!focusedFindingKey || !focusRequestId || !focusedIsShown) return;
    setFindingsOpen(true);
    // One frame, so a disclosure that was closed has mounted its cards before we look for one.
    const frame = requestAnimationFrame(() => {
      const card = Array.from(
        sectionRef.current?.querySelectorAll<HTMLElement>('[data-finding-key]') ?? [],
      ).find((node) => node.dataset.findingKey === focusedFindingKey);
      const calm =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      card?.scrollIntoView?.({ block: 'nearest', behavior: calm ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusedFindingKey, focusRequestId, focusedIsShown]);
  return (
    <section ref={sectionRef} className="min-w-0 max-w-full space-y-2 overflow-x-hidden">
      <p className={SECTION_LABEL}>Editorial critique</p>
      <p className={ITEM_META}>
        Automated critique; client approval is separate.{' '}
        {!isCurrent
          ? 'This review describes an older article version.'
          : report.status === 'unavailable'
            ? 'The critique did not finish.'
            : report.status === 'incomplete'
              ? 'Some dimensions could not be fully assessed.'
              : 'Complete for the supplied material.'}
      </p>
      <p className={ITEM_META}>
        {assessed}/6 dimensions assessed
        {needsAttention ? ` · ${needsAttention} need attention` : ''}
        {shownFindings.length
          ? ` · ${shownFindings.length} quoted finding${shownFindings.length === 1 ? '' : 's'}`
          : ''}
        {limitFindings.length
          ? ` · ${limitFindings.length} limit${limitFindings.length === 1 ? '' : 's'} of the review`
          : ''}
      </p>
      {isCurrent ? (
        <div className="flex flex-wrap items-center gap-2">
          {address?.available ? (
            <Button
              variant="neutral"
              size="sm"
              disabled={address.busy || refreshing}
              onClick={address.onAddress}
              aria-describedby="address-notes-note"
            >
              Address review notes
            </Button>
          ) : null}
          <Button
            variant={report.status !== 'reviewed' ? 'outline' : 'flat'}
            size="sm"
            className={report.status === 'reviewed' && !address?.available ? '-ml-3' : undefined}
            disabled={refreshing}
            onClick={() =>
              void onRefreshReview().catch((error) =>
                toast.error(
                  error instanceof Error
                    ? error.message
                    : 'Could not refresh the editorial review.',
                ),
              )
            }
          >
            {refreshing ? 'Refreshing…' : 'Refresh editorial review'}
          </Button>
        </div>
      ) : null}
      {isCurrent && address?.available ? (
        <p id="address-notes-note" className={ITEM_META}>
          {`Rewrites only ${
            address.openCount === 1
              ? 'the passage the open note quotes'
              : `the passages the ${address.openCount} open notes quote`
          }, with every edit checked: a citation, link, figure or date changes only with a quotation from the evidence. A note that cannot be fixed that way stays open with the reason. The result is a new version.`}
        </p>
      ) : isCurrent && address?.blockedReason ? (
        <p className={ITEM_META}>{address.blockedReason}</p>
      ) : null}
      {isCurrent && report.status === 'reviewed' ? (
        <p className={ITEM_META}>
          A check of unchanged text keeps this review; Refresh asks the reviewer again.
        </p>
      ) : null}
      <Collapsible open={findingsOpen} onOpenChange={setFindingsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="flat" size="sm" className="group -ml-2">
            <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
            {openFindings
              ? `${openFindings} editorial decision${openFindings === 1 ? '' : 's'} required`
              : shownFindings.length
                ? `${shownFindings.length} finding${shownFindings.length === 1 ? '' : 's'}${confirmedCount > 0 ? `, ${confirmedCount} confirmed by the claim check` : ', all reviewed'}`
                : flaggedDimensions.length
                  ? `What needs attention · ${flaggedDimensions.length}`
                  : 'Assessment and limitations'}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-4 overflow-x-hidden pt-2')}
        >
          <p className="break-words text-caption leading-relaxed">{report.summary}</p>
          {/* A dimension can be flagged `needs_attention` while producing no
              quoted finding — the critique judged the article weak on, say,
              evidence, without being able to point at one passage. The counts
              line said "1 need attention" and nothing below ever named it, so
              the reader saw a number with no subject and no way to act. */}
          {flaggedDimensions.length ? (
            <ul className="min-w-0 space-y-2">
              {flaggedDimensions.map((dimension) => (
                <li key={dimension.dimension} className="min-w-0 space-y-1">
                  <p className="break-words text-caption font-medium text-foreground">
                    {critiqueDimensionLabel(dimension.dimension)} needs attention
                  </p>
                  <p className={cn(ITEM_META, 'min-w-0 break-words')}>{dimension.reason}</p>
                </li>
              ))}
            </ul>
          ) : null}
          {shownFindings.length ? (
            <p className={ITEM_META}>
              Each quotation is validated against this saved article revision. Open the article to
              compare the surrounding passage before editing.
            </p>
          ) : null}
          {shownFindings.map((finding) => {
            const findingKey = articleCritiqueFindingKey(finding);
            const reviewed = reviewedFindingKeys.has(findingKey);
            const confirmed = reviewed ? undefined : confirmations?.get(findingKey);
            const notAddressed = reviewed
              ? undefined
              : address?.statusByQuote.get(flatQuote(finding.quote));
            return (
              <div
                key={findingKey}
                ref={findingKey === linkedFindingKey ? linkedFindingRef : undefined}
                data-finding-key={findingKey}
                // The Recommendation-card shape: what and why, the passage, the suggested change,
                // the decision. The focused card (pressed here, in the article, or by the header's
                // "Review next note") lifts with the same easing the rail's disclosures use.
                className={cn(
                  'min-w-0 max-w-full space-y-2.5 overflow-x-hidden rounded-xl border p-3 transition-[background-color,border-color] duration-200 ease-fluid',
                  focusedFindingKey === findingKey || linkedFindingKey === findingKey
                    ? 'border-foreground/25 bg-card/80'
                    : 'border-border/50 bg-card/40',
                )}
              >
                <div
                  {...(onFocusFinding && isCurrent && !reviewed
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        'aria-pressed': focusedFindingKey === findingKey,
                        onClick: () => onFocusFinding(findingKey),
                        onKeyDown: (event: React.KeyboardEvent) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          onFocusFinding(findingKey);
                        },
                      }
                    : {})}
                  className={cn(
                    'min-w-0 max-w-full space-y-2',
                    onFocusFinding &&
                      isCurrent &&
                      !reviewed &&
                      'cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  <div className="min-w-0 max-w-full space-y-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {/* The same hue as the passage's mark in the article: warning for an
                          evidence note, neutral for a prose note. */}
                      <span
                        aria-hidden
                        className={cn(
                          'size-1.5 shrink-0 rounded-full',
                          finding.dimension === 'evidence'
                            ? 'bg-warning'
                            : 'bg-muted-foreground/50',
                        )}
                      />
                      <p className={cn(ITEM_META, 'min-w-0 truncate')}>
                        {critiqueDimensionLabel(finding.dimension)}
                      </p>
                    </div>
                    <p className="min-w-0 max-w-full break-words text-caption font-medium text-foreground">
                      {finding.reason}
                    </p>
                  </div>
                  <blockquote
                    className={cn(
                      'line-clamp-2 min-w-0 max-w-full break-words border-l-2 pl-3 text-caption leading-relaxed text-muted-foreground transition-colors',
                      focusedFindingKey === findingKey || linkedFindingKey === findingKey
                        ? 'border-foreground/60'
                        : 'border-border',
                    )}
                  >
                    {finding.quote}
                  </blockquote>
                </div>
                {/* The suggested change. A finding carries a recommendation, not replacement
                    text (the critique is told never to invent a replacement fact), so there is no
                    before → after here; the only proposed text a note can have is an address
                    pass's REFUSED edit, which stays under "Couldn't address" with its verdict. */}
                <div className="min-w-0 max-w-full space-y-0.5 rounded-lg bg-muted/40 px-2.5 py-2">
                  <p className={MICRO_LABEL}>Suggested change</p>
                  <p className="min-w-0 max-w-full break-words text-caption leading-relaxed text-foreground/85">
                    {finding.suggestion}
                  </p>
                </div>
                {confirmed ? (
                  <div className="min-w-0 max-w-full space-y-1">
                    <StatusBadge tone="success" fill="outline">
                      Confirmed by the claim check
                    </StatusBadge>
                    <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>
                      {confirmationNote(confirmed)}
                    </p>
                  </div>
                ) : isCurrent ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {reviewed ? (
                      <>
                        <StatusBadge tone="success" fill="outline">
                          Reviewed
                        </StatusBadge>
                        <Button
                          variant="flat"
                          size="sm"
                          disabled={pendingFindingKey === findingKey}
                          onClick={() => void onSetFindingReviewed(finding, false)}
                        >
                          Return to review
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="neutral"
                          size="sm"
                          onClick={() => onOpenFinding(finding, true)}
                        >
                          Fix with AI
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenFinding(finding, false)}
                        >
                          Edit passage
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pendingFindingKey === findingKey}
                          onClick={() => void onSetFindingReviewed(finding, true)}
                        >
                          {pendingFindingKey === findingKey ? 'Saving…' : 'Accept as-is'}
                        </Button>
                        {tagNote ? (
                          <TagNoteButton topicId={tagNote.topicId} finding={finding} />
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
                {/* Detail behind disclosures, under a hairline: why an automatic fix was
                    refused, and the source excerpts the reviewer read. */}
                {(notAddressed && !confirmed) || finding.evidence.length ? (
                  <div className="min-w-0 max-w-full space-y-1.5 border-t border-border/40 pt-2">
                    {notAddressed && !confirmed ? (
                      <Collapsible className="min-w-0 max-w-full">
                        <CollapsibleTrigger className="group inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          <StatusBadge tone="warning" fill="outline">
                            Couldn&apos;t address
                          </StatusBadge>
                          <CaretDownIcon className="size-3 text-muted-foreground transition-transform duration-200 ease-fluid group-data-[state=closed]:-rotate-90" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className={cn(COLLAPSIBLE_MOTION, 'pt-1')}>
                          <AddressRefusalDetail refusal={notAddressed} />
                        </CollapsibleContent>
                      </Collapsible>
                    ) : null}
                    {finding.evidence.length ? (
                      <Collapsible className="min-w-0 max-w-full">
                        <CollapsibleTrigger className="rounded-sm text-caption text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          Supporting excerpts
                        </CollapsibleTrigger>
                        <CollapsibleContent
                          className={cn(
                            COLLAPSIBLE_MOTION,
                            'min-w-0 max-w-full space-y-2 overflow-x-hidden pt-2',
                          )}
                        >
                          {finding.evidence.map((source, sourceIndex) => (
                            <p
                              className={cn(ITEM_META, 'min-w-0 max-w-full break-all')}
                              key={`${source.sourceId}-${sourceIndex}`}
                            >
                              {source.sourceId}: {source.quote}
                            </p>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
          {report.strengths.length ? (
            <div className="space-y-1">
              <p className={SECTION_LABEL}>Preserve</p>
              {report.strengths.map((strength) => (
                <p className={cn(ITEM_META, 'break-words')} key={strength}>
                  {strength}
                </p>
              ))}
            </div>
          ) : null}
          {report.limitations.length || limitFindings.length ? (
            <div className="space-y-1">
              <p className={SECTION_LABEL}>Limits of this review</p>
              {limitFindings.map((finding) => (
                <div
                  key={articleCritiqueFindingKey(finding)}
                  className="min-w-0 max-w-full space-y-0.5"
                >
                  <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>
                    The reviewer could not check this against the source it was shown: “
                    {finding.quote.replace(/\s+/g, ' ').trim().slice(0, 220)}”
                  </p>
                  <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>
                    {finding.reason}
                  </p>
                </div>
              ))}
              {report.limitations.map((limitation) => (
                <p className={cn(ITEM_META, 'break-words')} key={limitation}>
                  {limitation}
                </p>
              ))}
            </div>
          ) : null}
          {isCurrent && address ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={cn(ITEM_META, 'min-w-0')}>
                After each check, address open notes automatically (once per version, at most twice
                in a row)
              </span>
              <SegmentedToggle
                variant="pills"
                size="sm"
                value={address.auto.mode}
                onValueChange={address.auto.onChange}
                ariaLabel="Address review notes automatically after each check"
                options={AUTO_ADDRESS_OPTIONS}
                className={address.auto.pending ? 'pointer-events-none opacity-60' : undefined}
              />
            </div>
          ) : null}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

const AUTO_ADDRESS_OPTIONS: ReadonlyArray<SegmentedToggleOption<AutoAddressMode>> = [
  { value: 'off', label: 'Off', tooltip: 'Nothing is addressed unless you press the button.' },
  {
    value: 'prose',
    label: 'Prose only',
    tooltip: 'Only notes about prose, structure and voice. Never a claim or a note about evidence.',
  },
  { value: 'all', label: 'All notes', tooltip: 'Every open note and every wrong-law claim.' },
];

const NO_BRANCH = '__none__';

/** A retained topic must be refiled explicitly before new article work uses it. */
function RefileTopic({
  topicId,
  pillars,
  saving,
  onSave,
}: {
  topicId: string;
  pillars: EditorialPillar[];
  saving: boolean;
  onSave: (pillarId: string, branchId: string | null) => Promise<void>;
}): JSX.Element {
  const [pillarId, setPillarId] = React.useState(pillars[0]?.id ?? '');
  const [branchId, setBranchId] = React.useState(NO_BRANCH);
  const branches = pillars.find((pillar) => pillar.id === pillarId)?.branches ?? [];

  return (
    <section className="space-y-2 border-l border-warning/40 pl-3">
      <div className="space-y-0.5">
        <p className={SECTION_LABEL}>Older taxonomy</p>
        <p className={ITEM_META}>
          This topic is retained from a prior pillar list. Refile it before drafting a new article.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select
          value={pillarId}
          onValueChange={(value) => {
            setPillarId(value);
            setBranchId(NO_BRANCH);
          }}
        >
          <SelectTrigger aria-label={`New content pillar for ${topicId}`}>
            <SelectValue placeholder="Choose a pillar" />
          </SelectTrigger>
          <SelectContent>
            {pillars.map((pillar) => (
              <SelectItem key={pillar.id} value={pillar.id}>
                {pillar.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={branchId} onValueChange={setBranchId} disabled={!branches.length}>
          <SelectTrigger aria-label={`New branch for ${topicId}`}>
            <SelectValue placeholder="No branch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_BRANCH}>No branch</SelectItem>
            {branches.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>
                {branch.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={!pillarId || saving}
        onClick={() => void onSave(pillarId, branchId === NO_BRANCH ? null : branchId)}
      >
        {saving ? 'Refiling…' : 'Refile topic'}
      </Button>
    </section>
  );
}

/** The state word at the end of a step row; the icon's colour says the same thing at a glance. */
const STEP_STATE_WORD: Record<ArticleStepState, string> = {
  done: 'Done',
  pending: 'To do',
  failed: 'Failed',
  unknown: 'No record',
};

function StepRow({ step, isLast }: { step: ArticleStep; isLast: boolean }): JSX.Element {
  const Icon =
    step.state === 'done'
      ? CheckCircleIcon
      : step.state === 'failed'
        ? WarningCircleIcon
        : step.state === 'pending'
          ? CircleIcon
          : CircleDashedIcon;
  // `done` / `pending` / `unknown` communicate three DIFFERENT facts —
  // passed, actively blocked, or no data yet — and collapsing `pending` into
  // the same grey as `unknown` is exactly the mistake design-system Law 2
  // now calls out by name: a state's job is to be told apart at a glance, and
  // greyscale removes the only thing it had to say. `unknown` (no data,
  // nothing to report) is the one state that legitimately stays neutral.
  const colorClass =
    step.state === 'done'
      ? 'text-success'
      : step.state === 'failed'
        ? 'text-destructive'
        : step.state === 'pending'
          ? 'text-warning'
          : 'text-muted-foreground';
  return (
    <li className="flex gap-2.5">
      {/*
       * THE LEADER LINE. Founder, 2026-09-07: _"That should be like leader lines
       * connecting those. I think we have on the homepage chat composer for
       * workflows, where it has leader lines connecting each of the points."_
       *
       * Built here rather than reused because neither existing primitive fits
       * and forcing one would be worse than a local implementation:
       * `stepper-bar.tsx` is a HORIZONTAL wizard bar keyed on `activeStepIndex`
       * (one current step, no per-step detail line), and `concept-pipeline.tsx`
       * says in its own header that its connected nouns "describe containment,
       * not progress; nothing here carries completion or generation state" —
       * which is the exact opposite of this. A vertical trail with a state and
       * a detail line per step has no primitive today. That absence is a
       * finding (design-system rule 33): if the content desk needs the same
       * trail on a board card, extract this rather than writing a second one.
       *
       * The line is drawn on the ICON RAIL, not between rows, so it survives a
       * step whose detail wraps to two lines.
       */}
      <div className="flex flex-col items-center self-stretch">
        <Icon weight="regular" className={cn('mt-0.5 size-3.5 shrink-0', colorClass)} aria-hidden />
        {!isLast ? (
          <span
            className={cn(
              'mt-1 w-px flex-1',
              step.state === 'done' ? 'bg-success/30' : 'bg-border',
            )}
            aria-hidden
          />
        ) : null}
      </div>
      {/* ONE LINE PER STEP: the label truncates and the state word sits at the end, so a narrow
          rail never wraps a label one word per line. A settled step's evidence line truncates too
          (full text in `title`); a failed or pending step's detail is the thing to act on, so it
          wraps rather than lose its reason. */}
      <div className={cn('min-w-0 flex-1', isLast ? 'pb-0' : 'pb-3')}>
        <div className="flex min-w-0 items-baseline gap-2">
          <p
            title={step.label}
            className={cn(
              'min-w-0 flex-1 truncate whitespace-nowrap text-caption',
              step.state === 'done' ? 'text-foreground/90' : 'text-muted-foreground',
            )}
          >
            {step.label}
          </p>
          <span className={cn(ITEM_META, 'shrink-0 whitespace-nowrap')}>
            {STEP_STATE_WORD[step.state]}
          </span>
        </div>
        {step.detail ? (
          <p
            title={step.detail}
            className={cn(
              ITEM_META,
              step.state === 'failed' || step.state === 'pending'
                ? 'break-words'
                : 'truncate whitespace-nowrap',
            )}
          >
            {step.detail}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * A label and its value. NOT A CARD.
 *
 * These were three bordered boxes stacked in a 300px rail — one each for the
 * title tag, the meta description and the slug. Founder, 2026-09-07: _"does
 * that really need to be three cards over there? That sidebar is starting to
 * look crazy messy."_
 *
 * The panel directly above already renders WRITTEN FOR and PROPOSED FROM as a
 * plain label over its value, with no box at all. So the boxes were not adding
 * structure, they were adding a THIRD visual treatment to a rail that already
 * had one — and three borders around three short strings is the design
 * system's own "card wrapper around static text" anti-pattern.
 *
 * The copy affordance stays, revealed on hover, because copying a title tag
 * into a CMS is the actual reason this section exists.
 */
/**
 * The domain, for a reader deciding whether a source is worth opening. A full
 * URL would wrap to three lines in this rail and a bare title does not say who
 * published it — "comptroller.texas.gov" answers that in one glance.
 */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function MetadataField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="group/meta flex items-start justify-between gap-2 py-1">
      <div className="min-w-0 space-y-0.5">
        <p className={ITEM_META}>{label}</p>
        <p className="break-words text-caption text-foreground/90">{value}</p>
      </div>
      <CopyButton
        getText={() => value}
        label={`Copy ${label.toLowerCase()}`}
        reveal="hover"
        className="shrink-0 opacity-0 transition-opacity group-hover/meta:opacity-100 focus-visible:opacity-100"
      />
    </div>
  );
}

function WebReadinessFindingRow({ finding }: { finding: WebReadinessFindingSummary }): JSX.Element {
  return (
    <li className="flex items-start gap-2">
      <WarningCircleIcon
        weight="regular"
        className={cn(
          'mt-0.5 size-3.5 shrink-0',
          finding.severity === 'fail' ? 'text-destructive' : 'text-warning',
        )}
        aria-hidden
      />
      <div className="min-w-0">
        <p className="text-caption text-foreground/90">{finding.detail}</p>
        <p className={ITEM_META}>{finding.fix}</p>
      </div>
    </li>
  );
}

/** Human labels for the stored doctrine identifiers; unknown checks remain identifiable. */
function checkLabel(id: string): string {
  const labels: Record<string, string> = {
    meta_title_valid: 'Title tag',
    meta_description_valid: 'Meta description',
    slug_valid: 'Page slug',
    no_ascii_diagrams: 'Readable diagrams',
    no_production_notes_in_body: 'No production notes in the body',
    images_have_alt: 'Image descriptions',
    no_heading_skipped: 'Heading order',
    no_invented_internal_urls: 'Internal links',
    question_headings_are_answered: 'Questions answered',
    named_authorities_are_linked: 'Authority citations',
    authority_links_are_specific: 'Specific authority links',
    links_are_real: 'Links go to real pages',
    avoids_absolute_promises: 'No absolute promises',
    no_self_awarded_verification: 'No self-awarded verification',
    cites_its_sources: 'Source citations',
    sources_were_retrieved: 'Sources actually read',
    claims_found_in_sources: 'Claims found in sources',
  };
  return labels[id] ?? id.replace(/_/g, ' ');
}
